import React, { useState, useEffect } from 'react';
import {
  Shield, TrendingDown, AlertTriangle, Target, Activity,
  BarChart3, Zap, Award, RefreshCw, Loader, Info,
  ArrowDown, ArrowUp, Minus, DollarSign, Percent,
  AlertCircle, CheckCircle, XCircle, Eye, Clock
} from 'lucide-react';

const PortfolioRiskAnalyzer = ({ portfolio }) => {
  const [loading, setLoading] = useState(false);
  const [riskData, setRiskData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    if (portfolio && portfolio.length > 0) {
      analyzeRisk();
    }
  }, [portfolio]);

  const analyzeRisk = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/portfolio/risk/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          holdings: portfolio
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to analyze portfolio risk');
      }
      
      const data = await response.json();
      setRiskData(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (score) => {
    if (score < 30) return '#10b981'; // Low risk - Green
    if (score < 60) return '#f59e0b'; // Medium risk - Yellow
    return '#ef4444'; // High risk - Red
  };

  const getRiskLabel = (score) => {
    if (score < 30) return 'Low Risk';
    if (score < 60) return 'Medium Risk';
    return 'High Risk';
  };

  if (loading) {
    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '3rem',
        textAlign: 'center'
      }}>
        <Loader size={32} className="spin" style={{ color: '#60a5fa', margin: '0 auto' }} />
        <p style={{ color: '#94a3b8', marginTop: '1rem' }}>Analyzing portfolio risk...</p>
      </div>
    );
  }

  if (error || !riskData) {
    return (
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        textAlign: 'center'
      }}>
        <p style={{ color: '#f87171', marginBottom: '1rem' }}>
          {error || 'No portfolio data available'}
        </p>
        <button
          onClick={analyzeRisk}
          style={{
            padding: '0.5rem 1rem',
            background: '#ef4444',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Retry Analysis
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Shield size={24} color="#60a5fa" />
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#f1f5f9' }}>
            Portfolio Risk Analyzer
          </h3>
        </div>
        
        <button
          onClick={analyzeRisk}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: '1px solid #475569',
            background: '#334155',
            color: '#cbd5e1',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#60a5fa';
            e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#475569';
            e.currentTarget.style.background = '#334155';
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Risk Score Card */}
      <div style={{
        background: `linear-gradient(135deg, ${getRiskColor(riskData.riskScore)}15, transparent)`,
        border: `2px solid ${getRiskColor(riskData.riskScore)}40`,
        borderRadius: '0.75rem',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '2rem' }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              Overall Risk Score
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: '700',
              color: getRiskColor(riskData.riskScore)
            }}>
              {riskData.riskScore}
            </div>
            <div style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: getRiskColor(riskData.riskScore)
            }}>
              {getRiskLabel(riskData.riskScore)}
            </div>
          </div>
          
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Portfolio Value</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f1f5f9' }}>
                  ${parseFloat(riskData.totalValue).toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Holdings</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f1f5f9' }}>
                  {riskData.numHoldings} positions
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>Sharpe Ratio</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f1f5f9' }}>
                  {riskData.sharpeRatio}
                </span>
              </div>
            </div>
            
            {/* Risk Score Bar */}
            <div style={{
              height: '12px',
              background: '#1e293b',
              borderRadius: '6px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{
                width: `${riskData.riskScore}%`,
                height: '100%',
                background: getRiskColor(riskData.riskScore),
                transition: 'width 0.5s ease'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.75rem', color: '#64748b' }}>
              <span>Low Risk</span>
              <span>High Risk</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #334155',
        paddingBottom: '0.5rem',
        overflowX: 'auto'
      }}>
        {['overview', 'black-swan', 'correlations', 'recommendations'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem 0.375rem 0 0',
              border: 'none',
              background: activeTab === tab ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
              color: activeTab === tab ? '#60a5fa' : '#cbd5e1',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab ? '600' : '400',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            {tab.replace('-', ' ')}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Key Risk Metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <MetricCard
              icon={<TrendingDown size={20} color="#ef4444" />}
              label="Value at Risk (95%)"
              value={`${riskData.var95}%`}
              description="Potential 1-day loss"
              color="#ef4444"
            />
            
            <MetricCard
              icon={<Activity size={20} color="#f59e0b" />}
              label="Volatility"
              value={`${riskData.volatility}%`}
              description="Annualized"
              color="#f59e0b"
            />
            
            <MetricCard
              icon={<Target size={20} color="#60a5fa" />}
              label="Sharpe Ratio"
              value={riskData.sharpeRatio}
              description="Risk-adjusted return"
              color="#60a5fa"
            />
            
            <MetricCard
              icon={<BarChart3 size={20} color="#8b5cf6" />}
              label="Beta"
              value={riskData.beta}
              description="vs S&P 500"
              color="#8b5cf6"
            />
            
            <MetricCard
              icon={<ArrowDown size={20} color="#ec4899" />}
              label="Max Drawdown"
              value={`${riskData.maxDrawdown}%`}
              description="Worst decline"
              color="#ec4899"
            />
            
            <MetricCard
              icon={<Award size={20} color="#10b981" />}
              label="Sortino Ratio"
              value={riskData.sortinoRatio}
              description="Downside risk"
              color="#10b981"
            />
          </div>

          {/* Advanced Metrics */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
              Advanced Risk Measures
            </h4>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.5rem' }}>
                <span style={{ color: '#cbd5e1', fontSize: '0.875rem' }}>Conditional VaR (95%)</span>
                <span style={{ color: '#ef4444', fontWeight: '600' }}>{riskData.cvar95}%</span>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.5rem' }}>
                <span style={{ color: '#cbd5e1', fontSize: '0.875rem' }}>Value at Risk (99%)</span>
                <span style={{ color: '#ef4444', fontWeight: '600' }}>{riskData.var99}%</span>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.5rem' }}>
                <span style={{ color: '#cbd5e1', fontSize: '0.875rem' }}>Annualized Return</span>
                <span style={{ color: parseFloat(riskData.annualizedReturn) > 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                  {riskData.annualizedReturn}%
                </span>
              </div>
            </div>
          </div>

          {/* Concentration Risk */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
              Concentration Risk
            </h4>
            
            {riskData.concentrationRisk && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                    Top 5 Holdings Concentration
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f1f5f9' }}>
                    {riskData.concentrationRisk.top5Percentage}%
                  </div>
                </div>
                
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {riskData.concentrationRisk.topHoldings.map((holding, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      borderRadius: '0.5rem'
                    }}>
                      <div>
                        <span style={{ fontWeight: '600', color: '#f1f5f9' }}>{holding.symbol}</span>
                        <span style={{ fontSize: '0.875rem', color: '#64748b', marginLeft: '0.5rem' }}>
                          ${parseFloat(holding.value).toLocaleString()}
                        </span>
                      </div>
                      <span style={{
                        fontWeight: '600',
                        color: parseFloat(holding.percentage) > 20 ? '#fbbf24' : '#10b981'
                      }}>
                        {holding.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
                
                {riskData.concentrationRisk.top5Percentage > 50 && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <AlertTriangle size={20} color="#fbbf24" />
                    <span style={{ fontSize: '0.875rem', color: '#fbbf24' }}>
                      High concentration risk detected. Consider diversifying your holdings.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Black Swan Tab */}
      {activeTab === 'black-swan' && (
        <div>
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Zap size={24} color="#fbbf24" />
              <h4 style={{ margin: 0, color: '#fbbf24', fontSize: '1.125rem', fontWeight: '600' }}>
                Black Swan Simulator
              </h4>
            </div>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Test how your portfolio would perform during major historical market crashes
            </p>
            
            {riskData.blackSwan && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {/* 2008 Crisis */}
                <BlackSwanCard
                  event={riskData.blackSwan.crisis2008}
                  currentValue={riskData.totalValue}
                />
                
                {/* COVID 2020 */}
                <BlackSwanCard
                  event={riskData.blackSwan.covid2020}
                  currentValue={riskData.totalValue}
                />
                
                {/* Dot-com Bubble */}
                <BlackSwanCard
                  event={riskData.blackSwan.dotComBubble}
                  currentValue={riskData.totalValue}
                />
              </div>
            )}
          </div>

          {/* Custom Crash Scenario */}
          {riskData.marketCrash20 && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '0.75rem',
              padding: '1.5rem'
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#ef4444', fontSize: '1rem', fontWeight: '600' }}>
                {riskData.marketCrash20.scenario}
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                    Current Value
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#f1f5f9' }}>
                    ${parseFloat(riskData.marketCrash20.currentValue).toLocaleString()}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                    Projected Value
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ef4444' }}>
                    ${parseFloat(riskData.marketCrash20.projectedValue).toLocaleString()}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                    Projected Loss
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ef4444' }}>
                    ${parseFloat(riskData.marketCrash20.loss).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Correlations Tab */}
      {activeTab === 'correlations' && (
        <div>
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
              Asset Correlation Matrix
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Shows how your holdings move together. Lower correlation = better diversification.
            </p>
            
            {riskData.correlationMatrix && riskData.correlationMatrix.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #334155' }}>
                        Symbol
                      </th>
                      {riskData.correlationMatrix.map((row, idx) => (
                        <th key={idx} style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #334155' }}>
                          {row.symbol}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {riskData.correlationMatrix.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td style={{ padding: '0.75rem', color: '#f1f5f9', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #334155' }}>
                          {row.symbol}
                        </td>
                        {row.correlations.map((corr, colIdx) => (
                          <td key={colIdx} style={{
                            padding: '0.75rem',
                            textAlign: 'center',
                            borderBottom: '1px solid #334155',
                            background: getCorrelationColor(corr),
                            color: '#f1f5f9',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {typeof corr === 'number' ? corr.toFixed(2) : corr}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', fontSize: '0.75rem' }}>
                  <span style={{ color: '#94a3b8' }}>Correlation Scale:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', background: getCorrelationColor(-1), borderRadius: '0.25rem' }} />
                    <span style={{ color: '#cbd5e1' }}>-1.0 (Inverse)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', background: getCorrelationColor(0), borderRadius: '0.25rem' }} />
                    <span style={{ color: '#cbd5e1' }}>0.0 (None)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', background: getCorrelationColor(1), borderRadius: '0.25rem' }} />
                    <span style={{ color: '#cbd5e1' }}>1.0 (Perfect)</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recommendations Tab */}
      {activeTab === 'recommendations' && (
        <div>
          {riskData.recommendations && riskData.recommendations.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {riskData.recommendations.map((rec, index) => (
                <div key={index} style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: `1px solid ${
                    rec.priority === 'high' ? 'rgba(239, 68, 68, 0.3)' :
                    rec.priority === 'medium' ? 'rgba(251, 191, 36, 0.3)' :
                    'rgba(96, 165, 250, 0.3)'
                  }`,
                  borderRadius: '0.75rem',
                  padding: '1.5rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                    <div style={{
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      background: rec.priority === 'high' ? 'rgba(239, 68, 68, 0.2)' :
                                 rec.priority === 'medium' ? 'rgba(251, 191, 36, 0.2)' :
                                 'rgba(96, 165, 250, 0.2)'
                    }}>
                      {rec.priority === 'high' ? <AlertTriangle size={24} color="#ef4444" /> :
                       rec.priority === 'medium' ? <AlertCircle size={24} color="#fbbf24" /> :
                       <Info size={24} color="#60a5fa" />}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <h5 style={{ margin: 0, color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
                          {rec.title}
                        </h5>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          background: rec.priority === 'high' ? 'rgba(239, 68, 68, 0.2)' :
                                     rec.priority === 'medium' ? 'rgba(251, 191, 36, 0.2)' :
                                     'rgba(96, 165, 250, 0.2)',
                          color: rec.priority === 'high' ? '#ef4444' :
                                rec.priority === 'medium' ? '#fbbf24' :
                                '#60a5fa',
                          textTransform: 'uppercase'
                        }}>
                          {rec.priority}
                        </span>
                      </div>
                      
                      <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                        {rec.description}
                      </p>
                      
                      <div style={{
                        padding: '0.75rem',
                        background: 'rgba(96, 165, 250, 0.1)',
                        borderRadius: '0.5rem',
                        borderLeft: '3px solid #60a5fa'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                          Recommended Action:
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#60a5fa', fontWeight: '600' }}>
                          {rec.action}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '0.75rem',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <CheckCircle size={48} color="#10b981" style={{ margin: '0 auto 1rem' }} />
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#10b981', fontSize: '1.125rem', fontWeight: '600' }}>
                Great Portfolio!
              </h4>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                No critical issues detected. Your portfolio appears well-balanced.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper Components
const MetricCard = ({ icon, label, value, description, color }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.5rem',
    padding: '1rem'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
      {icon}
      <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>{label}</span>
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: color || '#f1f5f9', marginBottom: '0.25rem' }}>
      {value}
    </div>
    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
      {description}
    </div>
  </div>
);

const BlackSwanCard = ({ event, currentValue }) => {
  const projectedValue = currentValue * (1 + event.loss / 100);
  const lossAmount = Math.abs(currentValue - projectedValue);
  
  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.5)',
      border: '1px solid #334155',
      borderRadius: '0.5rem',
      padding: '1.5rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h5 style={{ margin: '0 0 0.25rem 0', color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
            {event.name}
          </h5>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#94a3b8' }}>
            {event.description}
          </p>
        </div>
        <div style={{
          padding: '0.5rem 1rem',
          borderRadius: '0.375rem',
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ef4444' }}>
            {event.loss}%
          </div>
          <div style={{ fontSize: '0.75rem', color: '#f87171' }}>
            Loss
          </div>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            Current Value
          </div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#f1f5f9' }}>
            ${parseFloat(currentValue).toLocaleString()}
          </div>
        </div>
        
        <div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            Projected Value
          </div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#ef4444' }}>
            ${projectedValue.toLocaleString()}
          </div>
        </div>
        
        <div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            Potential Loss
          </div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#ef4444' }}>
            ${lossAmount.toLocaleString()}
          </div>
        </div>
        
        <div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            Duration
          </div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#f1f5f9' }}>
            {event.duration}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function for correlation color
function getCorrelationColor(corr) {
  const value = parseFloat(corr);
  if (isNaN(value)) return 'rgba(30, 41, 59, 0.5)';
  
  if (value > 0.7) return 'rgba(239, 68, 68, 0.3)'; // High positive - Red
  if (value > 0.3) return 'rgba(251, 191, 36, 0.3)'; // Medium positive - Yellow
  if (value > -0.3) return 'rgba(96, 165, 250, 0.3)'; // Low correlation - Blue
  if (value > -0.7) return 'rgba(139, 92, 246, 0.3)'; // Medium negative - Purple
  return 'rgba(16, 185, 129, 0.3)'; // High negative - Green (good for diversification)
}

export default PortfolioRiskAnalyzer;